/* Copyright (c) 2020-present CloudIO Inc */

import { useStateX, useStateXSelector } from '@cloudio-saas/statex';
import { Box, FormControl } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { helpTextSelector } from 'designer/state';
import { IconChooser } from 'icons/IconChooser';
import React, { memo } from 'react';
import { IconSelectProp, IconValue, ThemeColor } from 'componentregistry';
import { designPath } from 'designer/atoms';
import Icon from 'icons/Icon';
import { PropertyEditorProps } from './types';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  select: {
    flex: 1,
  },
  tip: { marginBottom: 8, marginLeft: 8 },
});

function IconSelectProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<IconValue>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<IconSelectProp, IconValue>) {
  const classes = useStyles();
  const [color] = useStateX<ThemeColor | undefined>(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: def.parentColorProp ?? '_',
    }),
    undefined,
  );
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (_value: IconValue) => {
    onChange(property, _value);
  };
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      disabled={isViewOnlyUser}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'stretch',
          width: '100%',
        }}
      >
        <IconChooser
          disabled={isViewOnlyUser}
          data-testid={property}
          className={classes.select}
          value={value}
          onChange={handleChange}
          label={def.label}
          parentColor={def.useParentColor ? undefined : color}
          color={def.useParentColor ? color : undefined}
        />
        {helpText ? (
          <Icon
            className={classes.tip}
            data-tip-at="top"
            data-tip-delay={0}
            data-tip={helpText}
            iconName="comment-exclamation"
          />
        ) : null}
      </Box>
    </FormControl>
  );
}

export default memo(IconSelectProperty);
